import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from './cn';

// Structural, presentational table primitive. Replaces every hand-rolled
// `<table className="table">` (ARCHITECTURE §8.2 / §8.16). The header row is
// sticky, so wrap in a bounded-height scroll region (`<Table>` is that region)
// and the header stays visible while the body scrolls. No data logic here — the
// US `<CrmDataTable>` composite maps columns/rows onto these parts.

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Applied to the scroll wrapper (set a max-height here to bound the scroll). */
  scrollClassName?: string;
}

/** Scroll region + `<table>`. Give `scrollClassName` a max-height to bound it. */
export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, scrollClassName, ...rest },
  ref,
) {
  return (
    <div className={cn('overflow-auto', scrollClassName)}>
      <table
        ref={ref}
        className={cn(
          'w-full border-separate border-spacing-0 text-[13px] text-foreground',
          className,
        )}
        {...rest}
      />
    </div>
  );
});

export const THead = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function THead(props, ref) {
    return <thead ref={ref} {...props} />;
  },
);

export const TBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TBody(props, ref) {
    return <tbody ref={ref} {...props} />;
  },
);

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Adds pointer cursor + hover affordance for clickable rows. */
  interactive?: boolean;
}

export const TR = forwardRef<HTMLTableRowElement, TRProps>(function TR(
  { className, interactive = false, ...rest },
  ref,
) {
  return (
    <tr
      ref={ref}
      className={cn(
        '[&>td]:transition-colors hover:[&>td]:bg-foreground/[0.03]',
        interactive && 'cursor-pointer',
        className,
      )}
      {...rest}
    />
  );
});

/** Sticky header cell. */
export const TH = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  function TH({ className, ...rest }, ref) {
    return (
      <th
        ref={ref}
        className={cn(
          'sticky top-0 z-[2] whitespace-nowrap border-b border-border bg-surface px-3 py-2.5',
          'text-left text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted',
          className,
        )}
        {...rest}
      />
    );
  },
);

export const TD = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  function TD({ className, ...rest }, ref) {
    return (
      <td
        ref={ref}
        className={cn('border-b border-border px-3 py-3 align-middle', className)}
        {...rest}
      />
    );
  },
);
